/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @version 1.0.1
 * @since 1.0.0
 */
import { memo } from 'react';

import { Input } from '../index';
import { IInputProps } from '../Input/Input';
import {
    DividerStyled,
    LeftContainerStyled,
    RangeContainerStyled,
    RightContainerStyled,
} from './styled/RangeInputStyled';

/**
 * @description Define props for RangeInput component.
 * @interface IRangeInputProps
 *
 * @property{ICurrentInputProps} left Props for left input.
 * @property{ICurrentInputProps} right Props for right input.
 * @property{string} spaceBetween Define space between left and right input.
 * @property{string} width Define input width.
 */
export interface IRangeInputProps {
    left: ICurrentInputProps;
    onEnter?: () => void;
    right: ICurrentInputProps;
    spaceBetween: string;
    widthSize: string;
}

/**
 * @description Define props for left & right input.
 * @interface ICurrentInputProps
 * @extends IInputProps
 *
 * @property{IInputProps} input Props for input field.
 * @property{number} maxValue Define max value for input field.
 * @property{number} minValue Define min value for input field.
 */
export interface ICurrentInputProps extends IInputProps {
    input?: IInputProps;
    maxValue?: number;
    minValue?: number;
}

/**
 * @description RangeInput component defines to input with divider between.
 * @class RangeInput component
 * @extends PureComponent<IRangeInputProps>
 */
const RangeInput = ({ left, onEnter, right, spaceBetween, widthSize  }: IRangeInputProps) => (
    <RangeContainerStyled className="range-input" widthSize={widthSize}>

            <LeftContainerStyled className="range-input__left-range">
                <Input
                    {...left.input}
                    autoFocus={true}
                    className="range-input__input"
                    max={left.maxValue}
                    min={left.minValue}
                    onEnter={onEnter}
                />
            </LeftContainerStyled>

            <DividerStyled className="range-input__divider" spaceBetween={spaceBetween}/>

            <RightContainerStyled className="range-input__right-range">
                <Input
                    {...right.input}
                    className="range-input__input"
                    max={right.maxValue}
                    min={right.minValue}
                    onEnter={onEnter}
                />
            </RightContainerStyled>

    </RangeContainerStyled>
);

RangeInput.defaultProps = {
    spaceBetween: '16px',
    widthSize: '256px',
    leftValueValid: true,
    rightValueValid: true,
};

export default memo(RangeInput);
